import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { BaseCellComponent } from '../base-cell.component';
import { ViewManager } from 'src/app/board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'bhub-datetime-picker-cell',
  standalone: false,
  templateUrl: './datetime-picker-cell.component.html',
  styleUrl: './datetime-picker-cell.component.scss'
})
export class DatetimePickerCellComponent extends BaseCellComponent implements OnInit, OnDestroy {
  @Input() viewManager: ViewManager | null = null;
  @Output() contextDataChange = new EventEmitter<{[contextKey: string]: any}>();
  
  // UI Properties
  labelText: string = '';
  selectedDate: Date | null = null;
  placeholder: string = '';
  
  // Configuration properties
  dataSource: 'context' | 'dataset' | 'none' = 'none';
  contextObjectKey: string = '';
  fieldName: string = '';
  datasetId: number | null = null;
  
  // DateTime configuration
  dateTimeMode: 'date' | 'time' | 'datetime' | 'day_range' | 'month_range' | 'year_range' = 'date';
  rangeStorageMode: 'existing' | 'new' = 'existing';
  rangeEndContextObjectKey: string = '';
  rangeEndFieldName: string = '';
  rangeStartContextVariable: string = '';
  rangeEndContextVariable: string = '';
  dateFormat: string = 'MM/dd/yyyy';
  timeFormat: string = '12h'; // '12h' or '24h'
  enableClear: boolean = true;
  minDate: Date | null = null;
  maxDate: Date | null = null;
  
  private destroy$ = new Subject<void>();
  
  constructor() {
    super();
  }
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  
  
  protected initializeCell(): void {
    this.updateFieldConfiguration();
    this.initializeContext();
    this.loadInitialValue();
  }
  
  private initializeContext(): void {
    if (!this.viewManager) return;
    
    // Initialize context data if needed
    if (!this.viewManager.contextData) {
      this.viewManager.contextData = {};
    }
    
    // Initialize context for datetime picker cells that manage their own context
    if (this.dataSource === 'context' && this.contextObjectKey) {
      // Initialize the context key if it doesn't exist
      if (!this.viewManager.contextData.hasOwnProperty(this.contextObjectKey)) {
        // Initialize with empty object that can hold field values
        this.viewManager.contextData[this.contextObjectKey] = {};
      }
    }
    
    // Initialize context variable for independent fields
    if (this.dataSource === 'none') {
      const contextVariable = this.getConfigValue('contextVariable', '');
      if (contextVariable) {
        const dateValue = this.selectedDate ? this.formatDateForStorage(this.selectedDate) : null;
        this.viewManager.contextData[contextVariable] = { value: dateValue };
      }
    }

    // Initialize range context variables so they're discoverable by other cells
    if (this.isRangeMode && this.rangeStorageMode === 'new') {
      if (this.rangeStartContextVariable && !this.viewManager.contextData.hasOwnProperty(this.rangeStartContextVariable)) {
        this.viewManager.contextData[this.rangeStartContextVariable] = { value: null };
      }
      if (this.rangeEndContextVariable && !this.viewManager.contextData.hasOwnProperty(this.rangeEndContextVariable)) {
        this.viewManager.contextData[this.rangeEndContextVariable] = { value: null };
      }
    }
  }
  
  protected onCellOrDataChange(): void {
    this.updateFieldConfiguration();
    this.initializeContext();
    this.loadInitialValue();
  }
  
  protected onCellUpdate(): void {
    this.updateFieldConfiguration();
    this.initializeContext();
    this.loadInitialValue();
  }
  
  protected onDataSet(data: any): void {
    // Update value when external data changes
    this.loadInitialValue();
  }
  
  private updateFieldConfiguration(): void {
    if (!this.cell) return;
    
    // Get basic configuration
    this.labelText = this.getConfigValue('label', '');
    this.placeholder = this.getConfigValue('placeholder', '');
    
    // Get datetime configuration
    this.dateTimeMode = this.getConfigValue('dateTimeMode', 'date') as 'date' | 'time' | 'datetime' | 'day_range' | 'month_range' | 'year_range';
    this.rangeStorageMode = this.getConfigValue('rangeStorageMode', 'existing') as 'existing' | 'new';
    this.rangeEndContextObjectKey = this.getConfigValue('rangeEndContextObjectKey', '');
    this.rangeEndFieldName = this.getConfigValue('rangeEndFieldName', '');
    this.rangeStartContextVariable = this.getConfigValue('rangeStartContextVariable', '');
    this.rangeEndContextVariable = this.getConfigValue('rangeEndContextVariable', '');
    this.dateFormat = this.getConfigValue('dateFormat', 'MM/dd/yyyy');
    this.timeFormat = this.getConfigValue('timeFormat', '12h');
    this.enableClear = this.getConfigValue('enableClear', true);
    
    // Get date constraints
    const minDateConfig = this.getConfigValue('minDate', null);
    const maxDateConfig = this.getConfigValue('maxDate', null);
    this.minDate = minDateConfig ? new Date(minDateConfig) : null;
    this.maxDate = maxDateConfig ? new Date(maxDateConfig) : null;
    
    // Determine data source
    this.dataSource = this.getConfigValue('dataSource', 'none') as 'context' | 'dataset' | 'none';
    
    // Get data binding configuration
    this.contextObjectKey = this.getDataBindingValue('dataObjectKey') || '';
    this.fieldName = this.getDataBindingValue('fieldName') || '';
    this.datasetId = this.getDataBindingValue('datasetId') || null;
  }
  
  private loadInitialValue(): void {
    if (this.dataSource === 'context' && this.contextObjectKey && this.fieldName && this.viewManager) {
      const dataObject = this.viewManager.contextData[this.contextObjectKey];
      if (dataObject) {
        let value = null;
        if (Array.isArray(dataObject) && dataObject.length > 0) {
          value = dataObject[0][this.fieldName];
        } else if (typeof dataObject === 'object' && dataObject !== null) {
          value = dataObject[this.fieldName];
        }
        
        // Convert string to Date if needed
        this.selectedDate = value ? this.parseDateFromStorage(value) : null;
      }
    } else if (this.dataSource === 'dataset' && this.datasetId && this.fieldName) {
      // For dataset source, we might want to load data from the dataset
      this.selectedDate = null;
    } else if (this.dataSource === 'none') {
      // Check if there's a stored value in context variable
      const contextVariable = this.getConfigValue('contextVariable', '');
      if (contextVariable && this.viewManager?.contextData?.[contextVariable]) {
        const contextObj = this.viewManager.contextData[contextVariable];
        // Value is stored as { value: dateString }
        const value = (typeof contextObj === 'object' && contextObj !== null) ? contextObj.value : contextObj;
        this.selectedDate = value ? this.parseDateFromStorage(value) : null;
      } else {
        this.selectedDate = null;
      }
    } else {
      // Independent field - no initial selection
      this.selectedDate = null;
    }
  }
  
  onDateChange(): void {
    // Compute start value (and end value for range modes)
    const startValue = this.selectedDate ? this.formatDateForStorage(this.computeRangeStart(this.selectedDate)) : null;
    const endValue = this.isRangeMode && this.selectedDate ? this.formatDateForStorage(this.computeRangeEnd(this.selectedDate)) : null;

    // Update the view manager context if configured
    if (this.dataSource === 'context' && this.contextObjectKey && this.fieldName && this.viewManager) {
      const dataObject = this.viewManager.contextData[this.contextObjectKey];

      if (dataObject && typeof dataObject === 'object' && !Array.isArray(dataObject)) {
        dataObject[this.fieldName] = startValue;

        // Store range end value
        if (this.isRangeMode && endValue && this.rangeEndContextObjectKey && this.rangeEndFieldName) {
          const endObj = this.viewManager.contextData[this.rangeEndContextObjectKey];
          if (endObj && typeof endObj === 'object' && !Array.isArray(endObj)) {
            endObj[this.rangeEndFieldName] = endValue;
          }
        }
      } else if (dataObject && Array.isArray(dataObject) && dataObject.length > 0) {
        dataObject[0][this.fieldName] = startValue;

        if (this.isRangeMode && endValue && this.rangeEndContextObjectKey && this.rangeEndFieldName) {
          const endObj = this.viewManager.contextData[this.rangeEndContextObjectKey];
          if (endObj && Array.isArray(endObj) && endObj.length > 0) {
            endObj[0][this.rangeEndFieldName] = endValue;
          } else if (endObj && typeof endObj === 'object' && !Array.isArray(endObj)) {
            endObj[this.rangeEndFieldName] = endValue;
          }
        }
      }
    } else if (this.isRangeMode && this.rangeStorageMode === 'new' && this.viewManager) {
      // Range mode with new context variables
      if (!this.viewManager.contextData) {
        this.viewManager.contextData = {};
      }
      const contextUpdates: {[key: string]: any} = {};

      if (this.rangeStartContextVariable) {
        this.viewManager.contextData[this.rangeStartContextVariable] = { value: startValue };
        contextUpdates[this.rangeStartContextVariable] = { value: startValue };
      }
      if (this.rangeEndContextVariable) {
        this.viewManager.contextData[this.rangeEndContextVariable] = { value: endValue };
        contextUpdates[this.rangeEndContextVariable] = { value: endValue };
      }

      if (Object.keys(contextUpdates).length > 0) {
        this.contextDataChange.emit(contextUpdates);
      }
    } else if (this.dataSource === 'dataset') {
      // For dataset source, no emit needed - data will be saved by Action Button
    } else {
      // For independent fields, update context variable if configured
      const contextVariable = this.getConfigValue('contextVariable', '');
      const dateValue = this.selectedDate ? this.formatDateForStorage(this.selectedDate) : null;

      if (contextVariable && this.viewManager) {
        if (!this.viewManager.contextData) {
          this.viewManager.contextData = {};
        }
        this.viewManager.contextData[contextVariable] = { value: dateValue };
        this.contextDataChange.emit({ [contextVariable]: this.viewManager.contextData[contextVariable] });
      }
    }
  }
  
  onMonthSelected(event: MatDatepickerInputEvent<Date>): void {
    if (event.value) {
      this.selectedDate = new Date(event.value.getFullYear(), event.value.getMonth(), 1, 0, 0, 0, 0);
    } else {
      this.selectedDate = null;
    }
    this.onDateChange();
  }

  onMonthPickerMonthSelected(date: Date, datepicker: MatDatepicker<Date>): void {
    this.selectedDate = new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
    datepicker.close();
    this.onDateChange();
  }

  onClearDate(): void {
    this.selectedDate = null;
    this.onDateChange();
  }

  private computeRangeStart(date: Date): Date {
    switch (this.dateTimeMode) {
      case 'day_range':
        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
      case 'month_range':
        return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
      case 'year_range':
        return new Date(date.getFullYear(), 0, 1, 0, 0, 0, 0);
      default:
        return date;
    }
  }

  private computeRangeEnd(date: Date): Date {
    switch (this.dateTimeMode) {
      case 'day_range':
        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 0);
      case 'month_range':
        // Last day of the selected month
        const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
        return new Date(date.getFullYear(), date.getMonth(), lastDay, 23, 59, 59, 0);
      case 'year_range':
        return new Date(date.getFullYear(), 11, 31, 23, 59, 59, 0);
      default:
        return date;
    }
  }

  // Format date for storage - matches Data Entry Page format
  private formatDateForStorage(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    // Return local time without timezone conversion in format: 2025-09-15T00:00:00
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  }

  // Parse date from storage format - handles both new and old formats for backward compatibility
  private parseDateFromStorage(value: string | Date): Date | null {
    if (!value) return null;
    
    if (value instanceof Date) {
      return value;
    }
    
    try {
      // Handle string values
      if (typeof value === 'string') {
        // Fix malformed date strings with negative hours (e.g., "2025-09-09T-5:00:00")
        let fixedValue = value;
        if (value.includes('T-')) {
          // Replace T- with T0 to fix negative hour issue
          fixedValue = value.replace(/T-/, 'T0');
        }
        
        // Check if it's in our standard format (YYYY-MM-DDTHH:mm:ss)
        if (/^\d{4}-\d{2}-\d{2}T\d{1,2}:\d{2}:\d{2}$/.test(fixedValue)) {
          // Parse the format as local time
          const [datePart, timePart] = fixedValue.split('T');
          const [year, month, day] = datePart.split('-').map(num => parseInt(num, 10));
          const timeParts = timePart.split(':');
          const hours = parseInt(timeParts[0], 10) || 0;
          const minutes = parseInt(timeParts[1], 10) || 0;
          const seconds = parseInt(timeParts[2], 10) || 0;
          
          const parsedDate = new Date(year, month - 1, day, hours, minutes, seconds);
          
          // Validate the parsed date
          if (!isNaN(parsedDate.getTime())) {
            return parsedDate;
          }
        }
        
        // Try to parse as standard JavaScript date string
        const parsedDate = new Date(fixedValue);
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate;
        }
      }
      
      // If we can't parse it, try one more time with the original value
      const lastAttempt = new Date(value as any);
      if (!isNaN(lastAttempt.getTime())) {
        return lastAttempt;
      }
      
      return null;
    } catch (error) {
      return null;
    }
  }

  // Format date for HTML input min/max attributes
  formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  
  // Get display format based on mode
  get displayFormat(): string {
    switch (this.dateTimeMode) {
      case 'date':
        return this.dateFormat;
      case 'time':
        return this.timeFormat === '12h' ? 'h:mm a' : 'HH:mm';
      case 'datetime':
        const timeFormat = this.timeFormat === '12h' ? 'h:mm a' : 'HH:mm';
        return `${this.dateFormat} ${timeFormat}`;
      default:
        return this.dateFormat;
    }
  }
  
  // Get input type for HTML input
  get inputType(): string {
    switch (this.dateTimeMode) {
      case 'date':
      case 'day_range':
        return 'date';
      case 'time':
        return 'time';
      case 'datetime':
        return 'datetime-local';
      case 'month_range':
        return 'month';
      case 'year_range':
        return 'number';
      default:
        return 'date';
    }
  }

  get isRangeMode(): boolean {
    return this.dateTimeMode === 'day_range' || this.dateTimeMode === 'month_range' || this.dateTimeMode === 'year_range';
  }
  
  // Get formatted value for HTML input
  get formattedValue(): string {
    if (!this.selectedDate) return '';

    const y = this.selectedDate.getFullYear();
    const m = String(this.selectedDate.getMonth() + 1).padStart(2, '0');
    const d = String(this.selectedDate.getDate()).padStart(2, '0');

    switch (this.dateTimeMode) {
      case 'date':
      case 'day_range':
        return `${y}-${m}-${d}`;
      case 'time':
        return `${String(this.selectedDate.getHours()).padStart(2, '0')}:${String(this.selectedDate.getMinutes()).padStart(2, '0')}`;
      case 'datetime':
        return `${y}-${m}-${d}T${String(this.selectedDate.getHours()).padStart(2, '0')}:${String(this.selectedDate.getMinutes()).padStart(2, '0')}`;
      case 'month_range':
        return `${y}-${m}`;
      case 'year_range':
        return String(y);
      default:
        return `${y}-${m}-${d}`;
    }
  }
  
  // Handle HTML input change
  onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    if (value) {
      switch (this.dateTimeMode) {
        case 'date':
        case 'day_range':
          const [year, month, day] = value.split('-').map(num => parseInt(num, 10));
          this.selectedDate = new Date(year, month - 1, day, 0, 0, 0, 0);
          break;
        case 'time':
          const today = new Date();
          const [hours, minutes] = value.split(':').map(num => parseInt(num, 10));
          this.selectedDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes, 0, 0);
          break;
        case 'datetime':
          this.selectedDate = new Date(value);
          break;
        case 'month_range':
          // value is "YYYY-MM"
          const [mYear, mMonth] = value.split('-').map(num => parseInt(num, 10));
          this.selectedDate = new Date(mYear, mMonth - 1, 1, 0, 0, 0, 0);
          break;
        case 'year_range':
          // value is a year number
          const yr = parseInt(value, 10);
          if (!isNaN(yr) && yr >= 1900 && yr <= 2200) {
            this.selectedDate = new Date(yr, 0, 1, 0, 0, 0, 0);
          }
          break;
      }
    } else {
      this.selectedDate = null;
    }

    this.onDateChange();
  }
}